import { fetcher } from '@shared/fetcher';
import { unmask } from '@shared/const/mask.ts';
import useSWRMutation from 'swr/mutation';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';

function login(url: string, { arg }: { arg: { phone: string } }) {
  return fetcher.post(url, { phone: unmask(arg.phone) });
}

export function useLogin() {
  const { trigger, isMutating } = useSWRMutation('/api/login', login);

  return { trigger, isMutating };
}

function profileUpdate(
  url: string,
  {
    arg,
  }: {
    arg: {
      first_name?: string;
      last_name?: string;
      address?: string;
      fcm_token?: string;
    };
  }
) {
  return fetcher.post(url, arg);
}

export function useProfileUpdate({ onSuccess }: { onSuccess?: () => void }) {
  const { trigger, isMutating } = useSWRMutation(
    '/api/profile',
    profileUpdate,
    { onSuccess }
  );

  return { trigger, isMutating };
}

function loginFinish(
  url: string,
  { arg }: { arg: { phone: string; code: string; fcmToken?: string } }
) {
  return fetcher.post<{ token: string }>(url, {
    phone: unmask(arg.phone),
    code: arg.code,
    ...(arg.fcmToken && { fcm_token: arg.fcmToken }),
  });
}

export function useOtp({
  onSuccess,
}: {
  onSuccess: (accessToken: string) => void;
}) {
  const { trigger, isMutating } = useSWRMutation(
    '/api/login/finish',
    loginFinish,
    {
      onSuccess: ({ data: { token } }) => {
        if (token) {
          localStorage.setItem('access_token', token);
          onSuccess(token);
        }
      },
    }
  );

  const timeoutRef = useRef<NodeJS.Timeout>(null);
  const counterRef = useRef<dayjs.Dayjs>(null);
  const [isShowCounter, setIsShowCounter] = useState(true);
  const [counterTimer, setCounterTime] = useState('03:00');

  function clearOtpInterval() {
    if (!timeoutRef.current) return;
    clearInterval(timeoutRef.current);
  }

  function resetCountdown() {
    setIsShowCounter(true);

    setCounterTime('03:00');
    counterRef.current = dayjs().add(3, 'minutes');
    timeoutRef.current = setInterval(() => {
      const timeMilliseconds = counterRef.current?.diff(dayjs()) || 0;
      if (timeMilliseconds > 0) {
        setCounterTime(dayjs(timeMilliseconds).format('mm:ss'));
      } else {
        setIsShowCounter(false);
        clearOtpInterval();
      }
    }, 1000);
  }

  useEffect(() => {
    resetCountdown();

    return () => {
      clearOtpInterval();
    };
  }, []);

  return { trigger, isMutating, counterTimer, isShowCounter, resetCountdown };
}

function logout(url: string) {
  return fetcher.post(url, {});
}

export function useLogout() {
  const { trigger, isMutating } = useSWRMutation('/api/logout', logout);

  return { trigger, isMutating };
}

function remove(url: string) {
  return fetcher.delete(url, {});
}

export function useRemoveProfile({ onSuccess }: { onSuccess?: () => void }) {
  const { trigger, isMutating } = useSWRMutation('/api/users', remove, {
    onSuccess,
  });

  return { trigger, isMutating };
}
